/*
  Drives the upsell popup and the cart add-on cards.

  Two modes, decided by `data-ttu-mode` on the modal:
    cart    — intercepts the first Check out click of the session
    product — intercepts the offer page buy form, adds to cart via AJAX,
              then confirms the add and offers the attached add-ons
*/
(() => {
  const modal = document.querySelector('[data-ttu-modal]');

  const addUrl = modal?.dataset.ttuAddUrl || '/cart/add.js';
  const cartUrl = modal?.dataset.ttuCartUrl || '/cart.js';
  const mode = modal?.dataset.ttuMode;
  let cartChanged = false;

  // Mirrors the Liquid `money` filter so the popup total matches the rest of
  // the page instead of Intl's own currency symbol.
  const formatMoney = (cents) => {
    const format = modal?.dataset.ttuMoneyFormat || '{{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);

    const group = (precision, thousands, decimal) => {
      const [whole, frac] = (cents / 100).toFixed(precision).split('.');
      return (
        whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands) + (frac ? decimal + frac : '')
      );
    };

    let value;
    switch (match && match[1]) {
      case 'amount_no_decimals':
        value = group(0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = group(2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = group(0, '.', ',');
        break;
      default:
        value = group(2, ',', '.');
    }

    return format.replace(placeholder, value);
  };

  const addToCart = (variantId) =>
    fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    }).then((res) => {
      if (!res.ok) throw new Error('add failed: ' + res.status);
      return res.json();
    });

  const refreshTotals = async () => {
    const targets = document.querySelectorAll('[data-ttu-total]');
    if (!targets.length) return;
    try {
      const res = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      targets.forEach((el) => (el.textContent = formatMoney(cart.total_price)));
    } catch (err) {
      console.error('[tt-upsell]', err);
    }
  };

  // Running tally of what the shopper has actually saved in this popup,
  // taken from each add-on's real compare-at price.
  let savedCents = 0;
  const addSavings = (cents) => {
    if (!cents) return;
    savedCents += cents;
    const box = document.querySelector('[data-ttu-savings]');
    const amount = document.querySelector('[data-ttu-savings-amount]');
    if (!box || !amount) return;
    amount.textContent = formatMoney(savedCents);
    box.hidden = false;
  };

  const setBusy = (btn, busy) => {
    btn.dataset.busy = busy ? '1' : '0';
    btn.classList.toggle('is-loading', busy);
  };

  /* ------------------------------------------------- add-on buttons -- */

  document.querySelectorAll('[data-ttu-add]').forEach((btn) => {
    const inModal = !!btn.closest('[data-ttu-modal]');

    btn.addEventListener('click', async () => {
      if (btn.dataset.busy === '1' || btn.dataset.done === '1') return;
      setBusy(btn, true);

      try {
        await addToCart(btn.dataset.variant);

        // Outside the popup we reload so the cart totals stay honest.
        if (!inModal) {
          window.location.reload();
          return;
        }

        setBusy(btn, false);
        btn.dataset.done = '1';
        btn.classList.add('is-added');
        btn.querySelector('.ttu__add-label').textContent = 'Added';
        btn.closest('[data-ttu-row]')?.classList.add('is-added');
        cartChanged = true;
        addSavings(Number(btn.dataset.save || 0));
        refreshTotals();
      } catch (err) {
        console.error('[tt-upsell]', err);
        setBusy(btn, false);
        btn.classList.add('is-error');
        btn.querySelector('.ttu__add-label').textContent = 'Try again';
      }
    });
  });

  if (!modal) return;

  /* -------------------------------------------------------- the popup -- */

  let lastFocus = null;

  const open = () => {
    lastFocus = document.activeElement;
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => modal.classList.add('is-open'));
    modal.querySelector('[data-ttu-checkout]')?.focus();
  };

  const close = () => {
    modal.classList.remove('is-open');
    modal.hidden = true;
    document.body.style.overflow = '';
    // Anything behind the popup (cart totals, header count) is now stale.
    if (cartChanged) {
      window.location.reload();
      return;
    }
    lastFocus?.focus();
  };

  const checkout = () => {
    sessionStorage.setItem('ttu-checkout-seen', '1');
    window.location.href = '/checkout';
  };

  modal.querySelectorAll('[data-ttu-close]').forEach((el) => el.addEventListener('click', close));
  modal.querySelectorAll('[data-ttu-checkout]').forEach((el) => el.addEventListener('click', checkout));

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !modal.hidden) close();
  });

  if (mode === 'cart') {
    // Capture phase so we win the race against the theme's own handler.
    document.addEventListener(
      'click',
      (event) => {
        if (!event.target.closest?.('[name="checkout"]')) return;
        if (sessionStorage.getItem('ttu-checkout-seen') === '1') return;

        event.preventDefault();
        event.stopPropagation();
        sessionStorage.setItem('ttu-checkout-seen', '1');
        open();
      },
      true
    );
  }

  if (mode === 'product') {
    document.querySelectorAll('form[data-ttu-buy]').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();

        const btn = form.querySelector('[name="add"]');
        if (btn?.dataset.busy === '1') return;
        if (btn) {
          btn.dataset.busy = '1';
          btn.setAttribute('aria-busy', 'true');
        }

        try {
          await addToCart(form.querySelector('[name="id"]').value);
          cartChanged = true;
          // Await, or the popup opens on the Liquid-rendered total — which was
          // Rs. 0.00 on a product page, since the cart was empty at render time.
          await refreshTotals();
          open();
        } catch (err) {
          console.error('[tt-upsell]', err);
          form.submit(); // fall back to the plain POST to /cart
          return;
        } finally {
          if (btn) {
            btn.dataset.busy = '0';
            btn.removeAttribute('aria-busy');
          }
        }
      });
    });
  }
})();
